// ─── Demandes de changement de garde + acquittement — porté MoPCal ──
// Workflow d'approbation entre parents : un parent PROPOSE un changement de
// garde pour un jour ; l'autre parent doit ACQUITTER (approuver/refuser). Quand
// approuvé, le changement est appliqué (garde du jour). Parent unique → appliqué
// directement et tracé (auto-acquitté). Collection `demandes` PARTAGÉE avec
// MoPCal (docs indexés par enfantId = calId) → visible dans les deux apps.
import { db, journaliser } from '@mop/core';
import {
  collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot,
  query, where, orderBy, getDoc, setDoc,
} from 'firebase/firestore';

export const TYPE_GARDE = 'changementGarde';
export const STATUT_ATTENTE = 'en_attente';
export const STATUT_APPROUVE = 'approuve';
export const STATUT_REFUSE = 'refuse';

// Applique la garde d'un jour (override), compatible MoPCal. Point
// d'étranglement commun aux deux chemins qui changent RÉELLEMENT la garde
// (parent unique auto-acquitté ET demande approuvée) — journalisé une fois
// ici plutôt que dans chacun des deux appelants (même patron que
// `sauvegarderJourOverride`, MoPCal `services/calendrier.js`).
async function appliquerGardeJour(calId, dk, garde, parUid, parNom) {
  await setDoc(doc(db, 'calendriers', calId, 'jours', dk),
    { garde: garde || null, estOverride: true, overridePar: parUid || null, overrideLe: new Date().toISOString() },
    { merge: true });
  journaliser(calId, {
    type: 'garde', action: 'modification',
    titre: dk, dateConcernee: dk, detail: garde,
    par: parUid || null, parNom: parNom || '',
  });
}

// Crée une demande de changement de garde (nécessite l'accord de l'autre parent).
export async function creerDemandeGarde({
  calId, dateKey, gardeActuelle, gardeProposee,
  demandePar, demandeParNom, autreParentUid, autreParentRole, message = '',
}) {
  await addDoc(collection(db, 'demandes'), {
    enfantId: calId,
    type: TYPE_GARDE,
    dateKey,
    gardeActuelle: gardeActuelle || null,
    gardeProposee: gardeProposee || null,
    demandePar, demandeParNom, message,
    statut: STATUT_ATTENTE,
    creeLe: new Date().toISOString(),
    approbations: {
      [autreParentUid]: { role: autreParentRole, requis: true, statut: STATUT_ATTENTE, note: '', traiteLe: null },
    },
  });
  journaliser(calId, {
    type: 'demande', action: 'creation',
    titre: dateKey, dateConcernee: dateKey, detail: gardeProposee,
    par: demandePar || null, parNom: demandeParNom || '',
  });
}

// Parent unique : applique le changement et trace une demande auto-acquittée.
export async function enregistrerGardeAutoAcquittee({
  calId, dateKey, gardeActuelle, gardeProposee, demandePar, demandeParNom, message = '',
}) {
  const now = new Date().toISOString();
  await appliquerGardeJour(calId, dateKey, gardeProposee, demandePar, demandeParNom);
  await addDoc(collection(db, 'demandes'), {
    enfantId: calId, type: TYPE_GARDE, dateKey,
    gardeActuelle: gardeActuelle || null, gardeProposee: gardeProposee || null,
    demandePar, demandeParNom, message,
    statut: STATUT_APPROUVE, auto: true,
    creeLe: now, approuveLe: now, approbations: {},
  });
}

// Approuve / refuse une demande ; applique la garde si approuvée définitivement.
export async function traiterDemande({ demandeId, traiteurUid, statut, note = '' }) {
  const ref = doc(db, 'demandes', demandeId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Demande introuvable');
  const demande = snap.data();
  const now = new Date().toISOString();

  const approbations = { ...demande.approbations };
  if (approbations[traiteurUid]) {
    approbations[traiteurUid] = { ...approbations[traiteurUid], statut, note, traiteLe: now };
  }
  const requis = Object.values(approbations).filter(a => a.requis);
  const tousAppr = requis.length > 0 && requis.every(a => a.statut === STATUT_APPROUVE);
  const unRefus = requis.some(a => a.statut === STATUT_REFUSE);

  let statutFinal = STATUT_ATTENTE;
  if (tousAppr) statutFinal = STATUT_APPROUVE;
  if (unRefus) statutFinal = STATUT_REFUSE;

  const update = { approbations, statut: statutFinal };
  if (statutFinal === STATUT_APPROUVE) {
    update.approuveLe = now;
    if (demande.type === TYPE_GARDE) {
      await appliquerGardeJour(demande.enfantId, demande.dateKey, demande.gardeProposee, demande.demandePar, demande.demandeParNom);
    }
  }
  if (statutFinal === STATUT_REFUSE) update.refuseLe = now;
  await updateDoc(ref, update);
  // Le traitement lui-même (approuvé/refusé), distinct du changement de garde
  // qu'il déclenche éventuellement (journalisé dans appliquerGardeJour).
  if (statutFinal === STATUT_APPROUVE || statutFinal === STATUT_REFUSE) {
    journaliser(demande.enfantId, {
      type: 'demande', action: 'modification',
      titre: demande.dateKey, dateConcernee: demande.dateKey, detail: statutFinal,
      par: traiteurUid || null, parNom: '',
    });
  }
  return statutFinal;
}

// Annule une demande (par son auteur).
export async function annulerDemande(demandeId) {
  await deleteDoc(doc(db, 'demandes', demandeId));
}

// Écoute les demandes d'un calendrier (triées par date décroissante).
//
// `onErreur(message)` remonté — même correction que sur les contacts/le
// journal/la messagerie : un refus de règle donnait « aucune demande » en
// silence, indiscernable d'un calendrier qui n'en a réellement aucune.
export function ecouterDemandes(calId, callback, onErreur) {
  if (!calId) { callback([]); return () => {}; }
  const q = query(collection(db, 'demandes'), where('enfantId', '==', calId), orderBy('creeLe', 'desc'));
  return onSnapshot(q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (e) => { callback([]); onErreur?.(e?.message || String(e)); });
}

// Nombre de demandes où `uid` doit encore acquitter (hors les siennes).
export function compterDemandesEnAttente(demandes, uid) {
  return (demandes || []).filter(d =>
    d.statut === STATUT_ATTENTE &&
    d.approbations?.[uid]?.statut === STATUT_ATTENTE &&
    d.demandePar !== uid,
  ).length;
}
